/**
 * CycleScheduleEditor — plain-language editor for a single obligation's JSONB schedule.
 *
 * Editor shows: How often (cycle) + "This obligation doesn't apply" + editable
 * Reference text + "More options" (Effective-from + Phased + Secondary filings).
 *
 * The "Starts from" control has been removed. The existing anchor on the obligation
 * record is preserved unchanged on every save (it still seeds the initial estimate).
 *
 * Collapse-on-save: outer panel uses controlled open state; onSuccess of any mutation
 * sets isOpen = false.
 *
 * Save payload:
 *   - Doesn't apply ON  → { status: "not_required", comment }
 *   - Simple controls   → { schedule: { existing anchor, chosen cycle, effectiveFrom,
 *                             referenceNote, secondary }, comment }
 *
 * Query invalidation: same 5 keys as other obligation mutations.
 */

import { useState, useId, useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, X, ChevronDown, Settings2, Pencil, Info } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { cn } from "../lib/cn";
import { patchObligation } from "../lib/dataFetchers";
import { extractApiError } from "../lib/api";
import { RenderReferenceText } from "./ui/RenderReferenceText";
import { SaveButtonWithHint } from "./ui/SaveButtonWithHint";
import { Button } from "./ui/Button";
import { Term } from "./ui/Term";
import { Tooltip } from "./ui/Tooltip";
import type {
  AnchorRule,
  AnchorSpec,
  CycleKind,
  CycleSpec,
  ObligationSchedule,
  PhasedEntry,
  SecondaryFiling,
  Obligation,
} from "../lib/types";

// ─── Constants ────────────────────────────────────────────────────────────────

const CYCLE_STRINGS = ["1y", "2y", "3y", "4y", "5y"];

// ─── Secondary filing frequency ───────────────────────────────────────────────
// "FrequencyKey" encodes the selected preset. "custom" reveals the N+unit inputs.
// "indefinite" maps to cycle="indefinite", no intervalNum/intervalUnit.
export type SfFrequencyKey =
  | "1mo"    // Monthly           intervalNum=1  intervalUnit="months"
  | "3mo"    // Quarterly         intervalNum=3  intervalUnit="months"
  | "6mo"    // Every 6 months    intervalNum=6  intervalUnit="months"
  | "1y"     // Yearly            cycle="1y"
  | "2y"     // Every 2 years     cycle="2y"
  | "3y"     // Every 3 years     cycle="3y"
  | "4y"     // Every 4 years     cycle="4y"
  | "5y"     // Every 5 years     cycle="5y"
  | "indefinite"
  | "custom";

interface SfFrequencyOption {
  key: SfFrequencyKey;
  labelKey: string; // i18n key in obligations namespace
}

const SF_FREQUENCY_OPTIONS: SfFrequencyOption[] = [
  { key: "1mo",        labelKey: "sf_freq_monthly" },
  { key: "3mo",        labelKey: "sf_freq_quarterly" },
  { key: "6mo",        labelKey: "sf_freq_every6mo" },
  { key: "1y",         labelKey: "sf_freq_yearly" },
  { key: "2y",         labelKey: "sf_freq_every2y" },
  { key: "3y",         labelKey: "sf_freq_every3y" },
  { key: "4y",         labelKey: "sf_freq_every4y" },
  { key: "5y",         labelKey: "sf_freq_every5y" },
  { key: "indefinite", labelKey: "sf_freq_indefinite" },
  { key: "custom",     labelKey: "sf_freq_custom" },
];

/**
 * Derive the SfFrequencyKey from a SecondaryFiling's cycle/interval fields.
 * Falls back to "1y" (yearly) for unrecognised values.
 */
function sfToFrequencyKey(sf: SecondaryFiling): SfFrequencyKey {
  if (sf.cycle === "indefinite") return "indefinite";
  if (sf.cycle && ["1y", "2y", "3y", "4y", "5y"].includes(sf.cycle)) {
    return sf.cycle as SfFrequencyKey;
  }
  if (sf.intervalUnit === "months") {
    if (sf.intervalNum === 1) return "1mo";
    if (sf.intervalNum === 3) return "3mo";
    if (sf.intervalNum === 6) return "6mo";
    // Any other month count → custom
    if (sf.intervalNum != null) return "custom";
  }
  if (sf.intervalUnit === "years" && sf.intervalNum != null) {
    const k = `${sf.intervalNum}y` as SfFrequencyKey;
    if (["1y", "2y", "3y", "4y", "5y"].includes(k)) return k;
    return "custom";
  }
  // Legacy free-text cycle strings that don't match any preset
  if (sf.cycle) {
    const match = /^(\d+)y$/.exec(sf.cycle);
    if (match) {
      const n = parseInt(match[1]!, 10);
      const k = `${n}y` as SfFrequencyKey;
      if (["1y", "2y", "3y", "4y", "5y"].includes(k)) return k;
    }
    return "custom";
  }
  return "1y";
}

/**
 * Map a SfFrequencyKey + custom values → the cycle/intervalNum/intervalUnit fields
 * that go into SecondaryFiling.
 */
function sfFrequencyToFields(
  key: SfFrequencyKey,
  customNum: number,
  customUnit: "months" | "years",
): Pick<SecondaryFiling, "cycle" | "intervalNum" | "intervalUnit"> {
  switch (key) {
    case "1mo":       return { cycle: null, intervalNum: 1,           intervalUnit: "months" };
    case "3mo":       return { cycle: null, intervalNum: 3,           intervalUnit: "months" };
    case "6mo":       return { cycle: null, intervalNum: 6,           intervalUnit: "months" };
    case "1y":        return { cycle: "1y", intervalNum: null,        intervalUnit: null };
    case "2y":        return { cycle: "2y", intervalNum: null,        intervalUnit: null };
    case "3y":        return { cycle: "3y", intervalNum: null,        intervalUnit: null };
    case "4y":        return { cycle: "4y", intervalNum: null,        intervalUnit: null };
    case "5y":        return { cycle: "5y", intervalNum: null,        intervalUnit: null };
    case "indefinite":return { cycle: "indefinite", intervalNum: null, intervalUnit: null };
    case "custom":    return { cycle: null, intervalNum: customNum,   intervalUnit: customUnit };
  }
}

// ─── Anchor rules for per-filing "Starts from" override ──────────────────────
const ANCHOR_RULE_KEYS: AnchorRule[] = [
  "anniversary",
  "fy_end_offset",
  "post_event_offset",
  "fixed_date",
  "fixed_window",
  "state_variable",
  "none",
];

// Simple-mode "How often" options
type HowOftenKey = "1y" | "2y" | "3y" | "4y" | "5y" | "manual" | "indefinite";
const HOW_OFTEN_OPTIONS: HowOftenKey[] = ["1y", "2y", "3y", "4y", "5y", "manual", "indefinite"];

/** Keys invalidated after any obligation mutation. */
function invalidationKeys(entityId: string) {
  return [
    { queryKey: ["entity-obligations", entityId] },
    { queryKey: ["renewals"] },
    { queryKey: ["entity", entityId] },
    { queryKey: ["changelog-recent"] },
    { queryKey: ["entity-renewal-status", entityId] },
  ];
}

// ─── Representable check ──────────────────────────────────────────────────────
// All anchor rules (including fixed_window) are now user-editable.
// Only gate on cycle kind for "non-representable" warning.

const REPRESENTABLE_CYCLE_KINDS = new Set<CycleKind>(["simple", "manual", "indefinite"]);

function isRepresentable(schedule: ObligationSchedule | null | undefined): boolean {
  if (!schedule) return true; // no schedule → open in simple mode with defaults
  return REPRESENTABLE_CYCLE_KINDS.has(schedule.cycle.kind);
}

// ─── EditorState — single source of truth for all editor local state ──────────

interface EditorState {
  // ── Anchor ────────────────────────────────────────────────────────────────
  anchorRule: AnchorRule;
  // anniversary (optional offset — e.g. UK NAR1: 42 days after anniversary)
  anniversaryOffsetValue: number | null; // null = no offset (exact anniversary)
  anniversaryOffsetUnit: "days" | "months"; // default "days"
  // fy_end_offset
  fyEndOffsetValue: number;          // renamed from fyEndMonth; default 0
  fyEndOffsetUnit: "months" | "days"; // NEW — was always "months"
  fyEndRangeMax: number | null;       // NEW — Italy: 180 (null for most)
  // post_event_offset
  postEventOffsetValue: number;       // NEW — was hardcoded 0
  postEventOffsetUnit: "months" | "days"; // NEW — was hardcoded "days"
  // fixed_date
  fixedMonth: number | null;
  fixedDay: number | null;            // day max clamped per fixedMonth
  // fixed_window
  fixedWindowMonth: number | null;    // NEW — was not editable at all
  fixedWindowDay: number | null;      // NEW

  // ── Cycle ─────────────────────────────────────────────────────────────────
  howOften: HowOftenKey;
  manualNextDue: string | null;

  // ── Doesn't apply ─────────────────────────────────────────────────────────
  doesntApply: boolean;

  // ── More Options ──────────────────────────────────────────────────────────
  moreOptionsOpen: boolean;
  effectiveFrom: string | null;
  phasedEnabled: boolean;
  advancedCycle: CycleSpec;
  secondary: SecondaryFiling[];
  referenceNote: string;
  refNoteEditing: boolean;

  // ── Comment ───────────────────────────────────────────────────────────────
  comment: string;
}

// Days-in-month table (Feb = 28 — conservative; backend validates leap-year edge cases)
const DAYS_IN_MONTH: Record<number, number> = {
  1: 31, 2: 28, 3: 31, 4: 30,  5: 31,  6: 30,
  7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function emptyAnchor(): AnchorSpec {
  return { rule: "anniversary" };
}

function emptyCycle(): CycleSpec {
  return { kind: "simple", cycle: "1y" };
}

function scheduleFromObligation(obl: Obligation): ObligationSchedule {
  if (obl.schedule) return obl.schedule;
  return {
    anchor: emptyAnchor(),
    cycle: emptyCycle(),
    referenceNote: obl.referenceNote ?? "",
    secondary: [],
  };
}

// ─── Module-level pure functions ──────────────────────────────────────────────

export function buildLocalState(obl: Obligation): EditorState {
  const sched = obl.schedule;
  const anchor = sched?.anchor ?? { rule: "anniversary" as AnchorRule };
  const doesntApply = obl.status === "not_required";
  const howOften: HowOftenKey =
    obl.status === "indefinite" ? "indefinite" :
    sched?.cycle.kind === "manual" ? "manual" :
    sched?.cycle.kind === "indefinite" ? "indefinite" :
    (sched?.cycle.kind === "simple"
      ? (CYCLE_STRINGS.includes(sched.cycle.cycle ?? "") ? sched.cycle.cycle as HowOftenKey : "1y")
      : "1y");
  return {
    anchorRule: anchor.rule,
    // anniversary offset (optional)
    anniversaryOffsetValue: anchor.rule === "anniversary" ? (anchor.offsetValue ?? null) : null,
    anniversaryOffsetUnit: anchor.rule === "anniversary" ? ((anchor.offsetUnit ?? "days") as "days" | "months") : "days",
    // fy_end_offset
    fyEndOffsetValue: anchor.rule === "fy_end_offset" ? (anchor.offsetValue ?? 0) : 0,
    fyEndOffsetUnit:  anchor.rule === "fy_end_offset" ? (anchor.offsetUnit ?? "months") : "months",
    fyEndRangeMax:    anchor.rule === "fy_end_offset" ? (anchor.rangeMaxValue ?? null) : null,
    // post_event_offset
    postEventOffsetValue: anchor.rule === "post_event_offset" ? (anchor.offsetValue ?? 0) : 0,
    postEventOffsetUnit:  anchor.rule === "post_event_offset" ? (anchor.offsetUnit ?? "days") : "days",
    // fixed_date
    fixedMonth: anchor.rule === "fixed_date" ? (anchor.fixedMonth ?? null) : null,
    fixedDay:   anchor.rule === "fixed_date" ? (anchor.fixedDay ?? null) : null,
    // fixed_window
    fixedWindowMonth: anchor.rule === "fixed_window" ? (anchor.fixedMonth ?? null) : null,
    fixedWindowDay:   anchor.rule === "fixed_window" ? (anchor.fixedDay ?? null) : null,
    howOften,
    manualNextDue: (howOften === "manual" || anchor.rule === "state_variable") ? (obl.nextDueDate ?? null) : null,
    doesntApply,
    effectiveFrom: sched?.effectiveFrom ?? null,
    phasedEnabled: sched?.cycle.kind === "phased",
    advancedCycle: sched?.cycle ?? { kind: "phased", phases: [], then: { kind: "indefinite" } },
    secondary: sched?.secondary ?? [],
    referenceNote: sched?.referenceNote ?? "",
    refNoteEditing: false,
    comment: "",
    moreOptionsOpen: false,
  };
}

export function buildAnchorFromState(s: EditorState): AnchorSpec {
  switch (s.anchorRule) {
    case "anniversary":
      // Only emit offsetValue/offsetUnit when a value is present (lossless round-trip:
      // plain anniversary → { rule: "anniversary" }, no stray fields).
      return s.anniversaryOffsetValue != null
        ? { rule: "anniversary", offsetValue: s.anniversaryOffsetValue, offsetUnit: s.anniversaryOffsetUnit }
        : { rule: "anniversary" };
    case "fy_end_offset":
      return {
        rule: "fy_end_offset",
        offsetValue:   s.fyEndOffsetValue,
        offsetUnit:    s.fyEndOffsetUnit,
        rangeMaxValue: s.fyEndOffsetUnit === "days" ? s.fyEndRangeMax : null,
      };
    case "post_event_offset":
      return {
        rule: "post_event_offset",
        offsetValue: s.postEventOffsetValue,
        offsetUnit:  s.postEventOffsetUnit,
      };
    case "fixed_date":
      return {
        rule: "fixed_date",
        fixedMonth: s.fixedMonth ?? 1,
        fixedDay:   s.fixedDay ?? 1,
      };
    case "fixed_window":
      return {
        rule: "fixed_window",
        fixedMonth: s.fixedWindowMonth ?? 1,
        fixedDay:   s.fixedWindowDay ?? 1,
      };
    case "state_variable":
      return { rule: "state_variable" };
    case "none":
      return { rule: "none" };
    default:
      return { rule: "anniversary" };
  }
}

function buildCycleFromHowOften(howOften: HowOftenKey): CycleSpec {
  if (howOften === "manual") return { kind: "manual" };
  if (howOften === "indefinite") return { kind: "indefinite" };
  return { kind: "simple", cycle: howOften };
}

/** Public alias used by round-trip tests (DLD §D.2). */
export function buildFinalCycle(s: EditorState): CycleSpec {
  if (s.phasedEnabled) {
    return s.advancedCycle.kind === "phased"
      ? s.advancedCycle
      : { kind: "phased", phases: [], then: { kind: "indefinite" } };
  }
  return buildCycleFromHowOften(s.howOften);
}

// ─── previewNextDue — pure client-side next-due estimation ──────────────────
// Mirrors the backend's compute_next_due logic for common anchor patterns.
// Returns ISO "YYYY-MM-DD" or null if the anchor field is missing or the
// rule does not support a client-side estimate (phased, state_variable, none).

export interface EntityForPreview {
  incorporationDateTyped?: string | null;
  fiscalYearEndMonth?: number | null;
  fiscalYearEndDay?: number | null;
  agmDate?: string | null;
}

function addMonths(d: Date, months: number): Date {
  const r = new Date(d);
  r.setMonth(r.getMonth() + months);
  return r;
}

function addDays(d: Date, days: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + days);
  return r;
}

function addYears(d: Date, years: number): Date {
  const r = new Date(d);
  r.setFullYear(r.getFullYear() + years);
  return r;
}

function cycleDeltaYears(howOften: HowOftenKey): number {
  const n = parseInt(howOften, 10);
  return Number.isNaN(n) ? 1 : n;
}

function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Result from previewNextDueDetailed — includes anchor transparency fields. */
export interface PreviewDetail {
  /** Next due date as ISO "YYYY-MM-DD", or null when not computable. */
  nextDue: string | null;
  /** ISO date of the anchor point used (incorporation date, FY-end, AGM date, etc.), or null. */
  anchorDate: string | null;
}

export function previewNextDue(
  state: EditorState,
  entity: EntityForPreview,
): string | null {
  return previewNextDueDetailed(state, entity).nextDue;
}

export function previewNextDueDetailed(
  state: EditorState,
  entity: EntityForPreview,
): PreviewDetail {
  const none: PreviewDetail = { nextDue: null, anchorDate: null };
  if (state.doesntApply) return none;
  if (state.phasedEnabled) return none; // phased too complex for client estimate
  if (state.howOften === "indefinite") return none;
  if (state.howOften === "manual") return { nextDue: state.manualNextDue, anchorDate: null };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const years = cycleDeltaYears(state.howOften);
  const effectiveFrom = state.effectiveFrom ? new Date(state.effectiveFrom) : null;

  const advancePastFloors = (candidate: Date): Date => {
    let d = candidate;
    // Advance past today — capped to prevent infinite loop on extreme future
    // dates. Uses `< today` (keeps a due date that equals today) to match the
    // backend engine, which only rolls forward when the date is strictly past.
    let iters = 0;
    while (d < today && ++iters < 500) {
      d = addYears(d, years);
    }
    // Advance past effectiveFrom floor
    if (effectiveFrom) {
      iters = 0;
      while (d < effectiveFrom && ++iters < 500) {
        d = addYears(d, years);
      }
    }
    return d;
  };

  switch (state.anchorRule) {
    case "anniversary": {
      if (!entity.incorporationDateTyped) return none;
      const inc = new Date(entity.incorporationDateTyped);
      if (isNaN(inc.getTime())) return none;
      // Apply the optional offset (mirrors backend anniversary + offset logic)
      const anniversaryThisYear = new Date(today.getFullYear(), inc.getMonth(), inc.getDate());
      const baseDate: Date = state.anniversaryOffsetValue != null
        ? (state.anniversaryOffsetUnit === "months"
            ? addMonths(anniversaryThisYear, state.anniversaryOffsetValue)
            : addDays(anniversaryThisYear, state.anniversaryOffsetValue))
        : anniversaryThisYear;
      return { nextDue: toIso(advancePastFloors(baseDate)), anchorDate: toIso(inc) };
    }
    case "fy_end_offset": {
      const fyMonth = entity.fiscalYearEndMonth;
      const fyDay   = entity.fiscalYearEndDay;
      if (!fyMonth || !fyDay) return none;
      // FY-end = last day of the chosen month/day in the current year
      const fyEnd = new Date(today.getFullYear(), fyMonth - 1, fyDay);
      const baseDate: Date = state.fyEndOffsetUnit === "months"
        ? addMonths(fyEnd, state.fyEndOffsetValue)
        : addDays(fyEnd, state.fyEndOffsetValue);
      return { nextDue: toIso(advancePastFloors(baseDate)), anchorDate: toIso(fyEnd) };
    }
    case "post_event_offset": {
      if (!entity.agmDate) return none;
      const agm = new Date(entity.agmDate);
      if (isNaN(agm.getTime())) return none;
      const baseDate: Date = state.postEventOffsetUnit === "months"
        ? addMonths(agm, state.postEventOffsetValue)
        : addDays(agm, state.postEventOffsetValue);
      return { nextDue: toIso(advancePastFloors(baseDate)), anchorDate: toIso(agm) };
    }
    case "fixed_date": {
      if (!state.fixedMonth || !state.fixedDay) return none;
      const candidate = new Date(today.getFullYear(), state.fixedMonth - 1, state.fixedDay);
      return { nextDue: toIso(advancePastFloors(candidate)), anchorDate: null };
    }
    case "fixed_window": {
      if (!state.fixedWindowMonth || !state.fixedWindowDay) return none;
      const candidate = new Date(today.getFullYear(), state.fixedWindowMonth - 1, state.fixedWindowDay);
      return { nextDue: toIso(advancePastFloors(candidate)), anchorDate: null };
    }
    case "state_variable":
      return { nextDue: state.manualNextDue, anchorDate: null };
    default:
      return none;
  }
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <p className="text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-2">
      {children}
    </p>
  );
}

interface StyledSelectProps {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  "aria-label"?: string;
  children: React.ReactNode;
  className?: string;
}

function StyledSelect({ id, value, onChange, disabled, children, className, ...rest }: StyledSelectProps): JSX.Element {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-label={rest["aria-label"]}
      className={cn(
        "h-9 px-3 rounded-lg border border-border bg-surface-2 text-[13px]",
        "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        className,
      )}
    >
      {children}
    </select>
  );
}

interface InlineDatePickerProps {
  value: string | null;
  onChange: (iso: string | null) => void;
  placeholder: string;
  ariaLabel: string;
}

function InlineDatePicker({ value, onChange, placeholder, ariaLabel }: InlineDatePickerProps): JSX.Element {
  // Native date input — reliable, consistent, and mobile-friendly (iOS picker).
  return (
    <input
      type="date"
      aria-label={ariaLabel}
      title={placeholder}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      className={cn(
        "min-h-[44px] min-w-[160px] px-3 rounded-lg border border-border bg-surface-2 text-base text-left",
        "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
        value ? "text-text" : "text-dim",
      )}
    />
  );
}

// ─── Phased cycle editor ─────────────────────────────────────────────────────

interface PhasedCycleEditorProps {
  cycle: CycleSpec;
  onChange: (c: CycleSpec) => void;
}

/** Friendly cycle dropdown — options use `how_often_*` labels, never raw "1y". */
function CycleDropdown({
  id,
  value,
  onChange,
  ariaLabel,
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  ariaLabel: string;
}): JSX.Element {
  const { t } = useTranslation("obligations");
  return (
    <StyledSelect
      id={id}
      value={value}
      onChange={onChange}
      aria-label={ariaLabel}
    >
      {CYCLE_STRINGS.map((c) => (
        <option key={c} value={c}>
          {t(`how_often_${c}` as `how_often_${typeof c}`)}
        </option>
      ))}
    </StyledSelect>
  );
}

/**
 * Lower-case a translated cycle label for mid-sentence use.
 * "Every year" → "every year"; "Chaque année" → "chaque année".
 * The label itself is not modified in the dropdown — only the summary uses this.
 */
function lc(s: string): string {
  if (!s) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
}

/** Plain-words summary built from phases + then, fully i18n'd. */
function PhasedPlainSummary({ cycle }: { cycle: CycleSpec }): JSX.Element {
  const { t } = useTranslation("obligations");
  const phases = cycle.phases ?? [];
  if (phases.length === 0) return <></>;

  const parts: string[] = phases.map((p, idx) => {
    // Lower-case the cycle label so it reads naturally mid-sentence
    const cycleLabel = lc(t(`how_often_${p.cycle}` as `how_often_${string}`, p.cycle));
    const count = p.durationYears;
    const yearsLabel = t(
      count === 1 ? "phased_plain_year_singular" : "phased_plain_year_plural",
      { count },
    );
    if (idx === 0) {
      return t("phased_plain_first_phase", { cycle: cycleLabel, years: yearsLabel });
    }
    return t("phased_plain_next_phase", { cycle: cycleLabel, years: yearsLabel });
  });

  const thenStr =
    cycle.then?.kind === "indefinite"
      ? t("phased_plain_then_stop")
      : cycle.then?.kind === "simple"
        ? t("phased_plain_then_keep", {
            cycle: lc(t(`how_often_${cycle.then.cycle ?? "1y"}` as `how_often_${string}`, cycle.then.cycle ?? "1y")),
          })
        : "";

  const summary = t("phased_plain_summary", { phases: parts.join(", "), then: thenStr });

  return (
    <p className="text-[11.5px] text-dim italic leading-relaxed">
      {summary}
    </p>
  );
}

function PhasedCycleEditor({ cycle, onChange }: PhasedCycleEditorProps): JSX.Element {
  const { t } = useTranslation("obligations");
  const uid = useId();
  const phases = cycle.phases ?? [];
  const then = cycle.then;

  // When phases.length <= 1 we default to simple view; once the user clicks
  // "Add another phase" we lock into multi-row view for the session.
  const [forceMultiRow, setForceMultiRow] = useState(false);
  const isSimpleView = !forceMultiRow && phases.length <= 1;

  const singlePhase: PhasedEntry = phases[0] ?? { durationYears: 1, cycle: "1y" };

  const updatePhase = (idx: number, patch: Partial<PhasedEntry>) => {
    const updated = phases.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    onChange({ ...cycle, phases: updated });
  };

  const addPhase = () => {
    const newPhases = phases.length === 0
      ? [singlePhase, { durationYears: 1, cycle: "1y" }]
      : [...phases, { durationYears: 1, cycle: "1y" }];
    setForceMultiRow(true);
    onChange({ ...cycle, phases: newPhases });
  };

  const removePhase = (idx: number) => {
    const updated = phases.filter((_, i) => i !== idx);
    onChange({ ...cycle, phases: updated });
    // Return to simple view if back down to 1 phase
    if (updated.length <= 1) setForceMultiRow(false);
  };

  const setTerminal = (kind: "indefinite" | "simple") => {
    onChange({
      ...cycle,
      then: kind === "indefinite" ? { kind: "indefinite" } : { kind: "simple", cycle: "1y" },
    });
  };

  // ── Terminal controls (shared between simple + multi-row) ─────────────────
  const TerminalControls = (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => setTerminal("indefinite")}
        className={cn(
          "px-2.5 py-1 text-[12px] rounded-lg border min-h-[44px] sm:min-h-[32px]",
          then?.kind === "indefinite"
            ? "border-accent bg-accent-soft text-accent font-semibold"
            : "border-border text-text hover:bg-surface-2",
        )}
      >
        {t("phased_then_indefinite")}
      </button>
      <button
        type="button"
        onClick={() => setTerminal("simple")}
        className={cn(
          "px-2.5 py-1 text-[12px] rounded-lg border min-h-[44px] sm:min-h-[32px]",
          then?.kind === "simple"
            ? "border-accent bg-accent-soft text-accent font-semibold"
            : "border-border text-text hover:bg-surface-2",
        )}
      >
        {t("phased_then_simple")}
      </button>
      {then?.kind === "simple" && (
        <CycleDropdown
          value={then.cycle ?? "1y"}
          onChange={(v) => onChange({ ...cycle, then: { kind: "simple", cycle: v } })}
          ariaLabel={t("cse_terminal_cycle_aria")}
        />
      )}
    </div>
  );

  // ── Simple view (one phase) ───────────────────────────────────────────────
  if (isSimpleView) {
    return (
      <div className="space-y-3">
        {/* Plain-English sentence row */}
        <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-surface-2 border border-border-2">
          <span className="text-[13px] text-text">{t("phased_simple_renew")}</span>
          <CycleDropdown
            id={`${uid}-simple-cycle`}
            value={singlePhase.cycle}
            onChange={(v) => {
              if (phases.length === 0) {
                onChange({ ...cycle, phases: [{ durationYears: singlePhase.durationYears, cycle: v }] });
              } else {
                updatePhase(0, { cycle: v });
              }
            }}
            ariaLabel={t("phased_simple_cycle_aria")}
          />
          <span className="text-[13px] text-text">{t("phased_simple_for_first")}</span>
          <input
            id={`${uid}-simple-dur`}
            type="number"
            min={1}
            value={singlePhase.durationYears}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              const dur = Number.isNaN(n) ? 1 : Math.max(1, n);
              if (phases.length === 0) {
                onChange({ ...cycle, phases: [{ durationYears: dur, cycle: singlePhase.cycle }] });
              } else {
                updatePhase(0, { durationYears: dur });
              }
            }}
            aria-label={t("phased_simple_duration_aria")}
            className="h-9 w-16 px-2 rounded-lg border border-border bg-surface text-[13px] text-center focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none"
          />
          <span className="text-[13px] text-text">
            {singlePhase.durationYears === 1 ? t("phased_simple_year") : t("phased_simple_years")}
          </span>
          <span className="text-[13px] text-muted">{t("phased_simple_then")}</span>
          {/* Terminal inline in simple mode */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setTerminal("indefinite")}
              className={cn(
                "px-2.5 py-1 text-[12px] rounded-lg border min-h-[44px] sm:min-h-[32px]",
                then?.kind === "indefinite"
                  ? "border-accent bg-accent-soft text-accent font-semibold"
                  : "border-border text-text hover:bg-surface-2",
              )}
            >
              {t("phased_then_indefinite")}
            </button>
            <button
              type="button"
              onClick={() => setTerminal("simple")}
              className={cn(
                "px-2.5 py-1 text-[12px] rounded-lg border min-h-[44px] sm:min-h-[32px]",
                then?.kind === "simple"
                  ? "border-accent bg-accent-soft text-accent font-semibold"
                  : "border-border text-text hover:bg-surface-2",
              )}
            >
              {t("phased_simple_keep_renewing")}
            </button>
            {then?.kind === "simple" && (
              <CycleDropdown
                value={then.cycle ?? "1y"}
                onChange={(v) => onChange({ ...cycle, then: { kind: "simple", cycle: v } })}
                ariaLabel={t("cse_terminal_cycle_aria")}
              />
            )}
          </div>
        </div>

        {/* Plain-words summary */}
        {phases.length > 0 && <PhasedPlainSummary cycle={cycle} />}

        {/* Advanced escape hatch */}
        <Button variant="ghost" onClick={addPhase}>
          <Plus className="h-3.5 w-3.5" aria-hidden /> {t("phased_add_another_phase")}
        </Button>
      </div>
    );
  }

  // ── Multi-row view (two or more phases) ──────────────────────────────────
  return (
    <div className="space-y-2">
      {phases.map((phase, idx) => (
        <div
          key={idx}
          className="flex flex-wrap items-end gap-3 p-3 rounded-lg bg-surface-2 border border-border-2"
        >
          <div className="flex flex-wrap items-end gap-2">
            <span className="text-[12px] text-muted self-end mb-[9px]">
              {idx === 0 ? t("phased_row_label_first") : t("phased_row_label_next")}
            </span>
            <div>
              <label
                htmlFor={`${uid}-phase-dur-${idx}`}
                className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5"
              >
                {t("cse_duration_years")}
              </label>
              <input
                id={`${uid}-phase-dur-${idx}`}
                type="number"
                min={1}
                value={phase.durationYears}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  updatePhase(idx, { durationYears: Number.isNaN(n) ? 1 : Math.max(1, n) });
                }}
                className="h-9 w-20 px-3 rounded-lg border border-border bg-surface text-[13px] focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none"
                aria-label={`Phase ${idx + 1} duration years`}
              />
            </div>
            <span className="text-[12px] text-muted self-end mb-[9px]">{t("phased_row_label_mid")}</span>
            <div>
              <label
                htmlFor={`${uid}-phase-cycle-${idx}`}
                className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1.5"
              >
                {t("cse_phase_cycle_label")}
              </label>
              <CycleDropdown
                id={`${uid}-phase-cycle-${idx}`}
                value={phase.cycle}
                onChange={(v) => updatePhase(idx, { cycle: v })}
                ariaLabel={`Phase ${idx + 1} cycle`}
              />
            </div>
          </div>
          <Button
            variant="ghost"
            size="xs"
            onClick={() => removePhase(idx)}
            aria-label={`Remove phase ${idx + 1}`}
            className="mb-px"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </div>
      ))}

      <Button variant="ghost" onClick={addPhase}>
        <Plus className="h-3.5 w-3.5" aria-hidden /> {t("phased_add_phase")}
      </Button>

      {phases.length > 0 && (
        <div className="space-y-2 pt-1">
          <span className="text-[12px] font-semibold text-muted">{t("phased_then_section_label")}</span>
          {TerminalControls}
        </div>
      )}

      {/* Plain-words summary */}
      {phases.length > 0 && <PhasedPlainSummary cycle={cycle} />}
    </div>
  );
}

// (FyOffsetSubInput, FixedDateSubInput, PostOffsetSubInput removed — "Starts from"
//  control is gone; anchor is preserved on save, not user-edited.)

// ─── InfoTooltip — reusable (i) icon with tooltip ────────────────────────────

interface InfoTooltipProps {
  content: string;
  side?: "top" | "bottom" | "left" | "right";
}

function InfoTooltip({ content, side = "top" }: InfoTooltipProps): JSX.Element {
  return (
    <Tooltip content={content} side={side}>
      {/* button type="button" — keyboard-focusable, ≥44px touch target via padding */}
      <button
        type="button"
        aria-label={content}
        className={cn(
          "inline-flex items-center justify-center",
          "w-[18px] h-[18px] min-h-[44px] p-[13px] -my-[13px]", // expand tap target to 44px
          "text-dim hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20 rounded",
          "cursor-default bg-transparent border-0",
        )}
      >
        <Info className="h-[13px] w-[13px] shrink-0" aria-hidden />
      </button>
    </Tooltip>
  );
}

// ─── Secondary filing editor ──────────────────────────────────────────────────
//
// Each filing card has:
//   - Filing name (text input)
//   - Frequency dropdown (preset list, replaces free-text cycle)
//   - Custom N + unit inputs (revealed when frequency === "custom")
//   - Reference note (text input)
//   - "More options" disclosure: "Starts from" anchor selector
//   - Trash / remove button
//
// Round-trips: sf.id (preserved on update, undefined on new), sf.anchor,
//   sf.intervalNum, sf.intervalUnit, sf.cycle.

interface SfCardState {
  frequencyKey: SfFrequencyKey;
  customNum: number;
  customUnit: "months" | "years";
  moreOpen: boolean;
}

interface SecondaryFilingEditorProps {
  filings: SecondaryFiling[];
  onChange: (filings: SecondaryFiling[]) => void;
  disabled?: boolean;
}

function SecondaryFilingEditor({ filings, onChange, disabled }: SecondaryFilingEditorProps): JSX.Element {
  const { t } = useTranslation("obligations");
  const uid = useId();

  // Check if any filing has ref note exceeding max length (exposed to parent for Save disable)
  const hasRefNoteError = filings.some((f) => (f.referenceNote ?? "").length > 500);

  // Per-card UI state (frequency key, custom fields, more-options toggle).
  // Indexed by filing position; rebuilt when filing list changes length or id changes.
  const [cardStates, setCardStates] = useState<SfCardState[]>(() =>
    filings.map((f) => ({
      frequencyKey: sfToFrequencyKey(f),
      customNum: f.intervalNum ?? 1,
      customUnit: f.intervalUnit ?? "months",
      moreOpen: false,
    })),
  );

  // Keep cardStates in sync when the filings array length changes (add/remove)
  // or when an external prop update comes in (e.g. obligation refetch resets state).
  const [prevFilingsLength, setPrevFilingsLength] = useState(filings.length);
  const [prevFilingIds, setPrevFilingIds] = useState(() => filings.map((f) => f.id ?? ""));
  if (
    filings.length !== prevFilingsLength ||
    filings.some((f, i) => (f.id ?? "") !== prevFilingIds[i])
  ) {
    setPrevFilingsLength(filings.length);
    setPrevFilingIds(filings.map((f) => f.id ?? ""));
    setCardStates(
      filings.map((f) => ({
        frequencyKey: sfToFrequencyKey(f),
        customNum: f.intervalNum ?? 1,
        customUnit: f.intervalUnit ?? "months",
        moreOpen: false,
      })),
    );
  }

  const updateCardState = (idx: number, patch: Partial<SfCardState>) => {
    setCardStates((cs) => cs.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const update = (idx: number, patch: Partial<SecondaryFiling>) => {
    onChange(filings.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };

  /**
   * Apply a frequency change: update both card UI state AND the filing's
   * cycle/intervalNum/intervalUnit fields so the parent always has the
   * latest structured payload.
   */
  const applyFrequency = (idx: number, key: SfFrequencyKey) => {
    const cs = cardStates[idx]!;
    const fields = sfFrequencyToFields(key, cs.customNum, cs.customUnit);
    updateCardState(idx, { frequencyKey: key });
    update(idx, fields);
  };

  const applyCustomNum = (idx: number, num: number) => {
    const cs = cardStates[idx]!;
    const fields = sfFrequencyToFields("custom", num, cs.customUnit);
    updateCardState(idx, { customNum: num });
    update(idx, fields);
  };

  const applyCustomUnit = (idx: number, unit: "months" | "years") => {
    const cs = cardStates[idx]!;
    const fields = sfFrequencyToFields("custom", cs.customNum, unit);
    updateCardState(idx, { customUnit: unit });
    update(idx, fields);
  };

  const remove = (idx: number) => {
    onChange(filings.filter((_, i) => i !== idx));
    setCardStates((cs) => cs.filter((_, i) => i !== idx));
  };

  const add = () => {
    // New filings start with id=undefined so backend assigns a new cuid.
    onChange([
      ...filings,
      { id: undefined, name: "", cycle: "1y", intervalNum: null, intervalUnit: null, anchor: null, referenceNote: "" },
    ]);
    setCardStates((cs) => [
      ...cs,
      { frequencyKey: "1y", customNum: 1, customUnit: "months", moreOpen: false },
    ]);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <SectionLabel>{t("secondary_filings_label")}</SectionLabel>
        <InfoTooltip content={t("sf_tooltip_additional_filings")} side="right" />
      </div>
      {filings.map((filing, idx) => {
        const cs = cardStates[idx] ?? { frequencyKey: "1y", customNum: 1, customUnit: "months" as const, moreOpen: false };
        return (
          <div
            key={filing.id ?? idx}
            className="rounded-lg bg-surface-2 border border-border-2 p-3 space-y-3"
          >
            {/* ── Row 1: name + remove ── */}
            <div className="flex items-end gap-2">
              <div className="flex-1 min-w-[120px]">
                <label
                  htmlFor={`${uid}-sf-name-${idx}`}
                  className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1"
                >
                  {t("sf_name_label")}
                </label>
                <input
                  id={`${uid}-sf-name-${idx}`}
                  type="text"
                  value={filing.name}
                  disabled={disabled}
                  onChange={(e) => update(idx, { name: e.target.value })}
                  placeholder={t("sf_name_placeholder")}
                  className="text-base h-9 w-full px-3 rounded-lg border border-border bg-surface text-[13px] focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none disabled:opacity-50"
                />
              </div>
              <Button
                variant="ghost"
                size="xs"
                disabled={disabled}
                onClick={() => remove(idx)}
                aria-label={`${t("sf_remove_aria")} ${idx + 1}`}
                className="mb-px"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </Button>
            </div>

            {/* ── Row 2: frequency + reference note ── */}
            <div className="flex flex-wrap items-end gap-2">
              {/* Frequency dropdown */}
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <label
                    htmlFor={`${uid}-sf-freq-${idx}`}
                    className="text-[10px] font-bold tracking-[.05em] uppercase text-dim"
                  >
                    {t("sf_freq_label")}
                  </label>
                  <InfoTooltip content={t("sf_tooltip_frequency")} />
                </div>
                <StyledSelect
                  id={`${uid}-sf-freq-${idx}`}
                  value={cs.frequencyKey}
                  onChange={(v) => applyFrequency(idx, v as SfFrequencyKey)}
                  disabled={disabled}
                  aria-label={t("sf_freq_label")}
                  className="min-w-[160px]"
                >
                  {SF_FREQUENCY_OPTIONS.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {t(opt.labelKey)}
                    </option>
                  ))}
                </StyledSelect>
              </div>

              {/* Custom interval inputs — only when "custom" selected */}
              {cs.frequencyKey === "custom" && (
                <div className="flex items-end gap-2">
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <label
                        htmlFor={`${uid}-sf-cn-${idx}`}
                        className="text-[10px] font-bold tracking-[.05em] uppercase text-dim"
                      >
                        {t("sf_custom_every_label")}
                      </label>
                      <InfoTooltip content={t("sf_tooltip_custom_interval")} />
                    </div>
                    <input
                      id={`${uid}-sf-cn-${idx}`}
                      type="number"
                      min={1}
                      value={cs.customNum}
                      disabled={disabled}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        applyCustomNum(idx, Number.isNaN(n) ? 1 : Math.max(1, n));
                      }}
                      className="text-base h-9 w-16 px-3 rounded-lg border border-border bg-surface text-[13px] focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none disabled:opacity-50"
                      aria-label={t("sf_custom_every_label")}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor={`${uid}-sf-cu-${idx}`}
                      className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1"
                    >
                      {t("sf_custom_unit_label")}
                    </label>
                    <StyledSelect
                      id={`${uid}-sf-cu-${idx}`}
                      value={cs.customUnit}
                      onChange={(v) => applyCustomUnit(idx, v as "months" | "years")}
                      disabled={disabled}
                      aria-label={t("sf_custom_unit_label")}
                    >
                      <option value="months">{t("interval_unit_months")}</option>
                      <option value="years">{t("interval_unit_years")}</option>
                    </StyledSelect>
                  </div>
                </div>
              )}

              {/* Reference note (maxLength=500) */}
              {(() => {
                const refLen = (filing.referenceNote ?? "").length;
                const refErr = refLen > 500;
                return (
                  <div className="flex-1 min-w-[140px]">
                    <label
                      htmlFor={`${uid}-sf-ref-${idx}`}
                      className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1"
                    >
                      {t("sf_ref_label")}
                    </label>
                    <input
                      id={`${uid}-sf-ref-${idx}`}
                      type="text"
                      value={filing.referenceNote ?? ""}
                      disabled={disabled}
                      maxLength={500}
                      onChange={(e) => update(idx, { referenceNote: e.target.value })}
                      placeholder={t("sf_ref_placeholder")}
                      className={cn(
                        "text-base h-9 w-full px-3 rounded-lg border bg-surface text-[13px]",
                        "focus:ring-2 focus:ring-accent/14 focus:outline-none disabled:opacity-50",
                        refErr
                          ? "border-red-400 focus:border-red-400"
                          : "border-border focus:border-accent",
                      )}
                    />
                    {refErr && (
                      <p className="text-[11px] text-red-600 mt-1">
                        {t("sf_ref_too_long", { count: refLen })}
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* ── "More options" disclosure: per-filing anchor override ── */}
            <details
              open={cs.moreOpen}
              onToggle={(e) =>
                updateCardState(idx, { moreOpen: (e.currentTarget as HTMLDetailsElement).open })
              }
              className="group"
            >
              <summary className="flex items-center gap-1.5 text-[11.5px] font-semibold text-muted hover:text-text cursor-pointer list-none select-none focus-visible:ring-2 focus-visible:ring-accent/20 rounded">
                <ChevronDown
                  className={cn(
                    "h-3 w-3 transition-transform",
                    cs.moreOpen && "rotate-180",
                  )}
                  aria-hidden
                />
                {t("sf_more_options_label")}
              </summary>
              <div className="mt-2 pl-1 space-y-2">
                {/* Starts from — anchor override */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <label
                      htmlFor={`${uid}-sf-anchor-${idx}`}
                      className="text-[10px] font-bold tracking-[.05em] uppercase text-dim"
                    >
                      {t("sf_starts_from_label")}
                    </label>
                    <InfoTooltip
                      content={
                        filing.anchor == null
                          ? t("sf_tooltip_starts_from_same_as_parent")
                          : t("sf_tooltip_starts_from")
                      }
                    />
                  </div>
                  <StyledSelect
                    id={`${uid}-sf-anchor-${idx}`}
                    value={filing.anchor == null ? "inherit" : filing.anchor.rule}
                    onChange={(v) => {
                      if (v === "inherit") {
                        update(idx, { anchor: null });
                      } else {
                        update(idx, { anchor: { rule: v as AnchorRule } });
                      }
                    }}
                    disabled={disabled}
                    aria-label={t("sf_starts_from_label")}
                    className="min-w-[220px]"
                  >
                    <option value="inherit">{t("sf_anchor_inherit")}</option>
                    {ANCHOR_RULE_KEYS.map((rule) => (
                      <option key={rule} value={rule}>
                        {t(`anchor_${rule}`)}
                      </option>
                    ))}
                  </StyledSelect>
                  {filing.anchor == null && (
                    <p className="text-[11px] text-dim mt-1">
                      {t("sf_anchor_inherit_hint")}
                    </p>
                  )}
                </div>
              </div>
            </details>
          </div>
        );
      })}
      <Button variant="ghost" onClick={add} disabled={disabled || hasRefNoteError}>
        <Plus className="h-3.5 w-3.5" aria-hidden /> {t("sf_add_button")}
      </Button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface CycleScheduleEditorProps {
  entityId: string;
  obligation: Obligation;
  /** Entity anchor fields used for the live next-due preview. Optional — preview is skipped when absent. */
  entity?: EntityForPreview;
}

export function CycleScheduleEditor({ entityId, obligation, entity }: CycleScheduleEditorProps): JSX.Element {
  const { t } = useTranslation("obligations");
  const { t: tTF } = useTranslation("typed_fields");
  const MONTH_KEYS_OBL = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"] as const;
  const monthName = (m: number | null): string => {
    if (!m) return "?";
    const key = MONTH_KEYS_OBL[m - 1];
    return key ? tTF(key) : String(m);
  };
  const qc = useQueryClient();
  const uid = useId();

  // ── Outer controlled open state ───────────────────────────────────────────
  const [isOpen, setIsOpen] = useState(false);

  // ── Single EditorState ────────────────────────────────────────────────────
  const [state, setState] = useState<EditorState>(() => buildLocalState(obligation));

  // ── Sync guard: reset state when obligation prop changes ──────────────────
  const [prevObligationKey, setPrevObligationKey] = useState(
    `${obligation.id}:${obligation.status}:${JSON.stringify(obligation.schedule)}`
  );
  const currentKey = `${obligation.id}:${obligation.status}:${JSON.stringify(obligation.schedule)}`;
  if (currentKey !== prevObligationKey) {
    setPrevObligationKey(currentKey);
    setState(buildLocalState(obligation));
  }

  // ── Representable check ───────────────────────────────────────────────────
  const representable = useMemo(() => isRepresentable(obligation.schedule), [obligation.schedule]);

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      patchObligation(entityId, obligation.id, payload),
    onSuccess: () => {
      toast.success(t("update_success"));
      invalidationKeys(entityId).forEach((k) => void qc.invalidateQueries(k));
      setIsOpen(false);
      setState((s) => ({ ...s, moreOptionsOpen: false, refNoteEditing: false, comment: "" }));
    },
    onError: (err) => toast.error(extractApiError(err) || t("update_error")),
  });

  const handleSave = () => {
    if (state.doesntApply) {
      // Explicitly clear any orphaned nextDueDate when the obligation is
      // marked not-applicable — otherwise a previously-set date would survive
      // the status flip and keep showing in the matrix.
      mutation.mutate({
        status: "not_required",
        nextDueDate: null,
        comment: state.comment.trim() || null,
      });
      return;
    }

    // Build anchor from state (seeded from the existing obligation's anchor — preserved on save).
    const anchor = buildAnchorFromState(state);
    const cycle = state.phasedEnabled
      ? (state.advancedCycle.kind === "phased" ? state.advancedCycle : { kind: "phased" as const, phases: [], then: { kind: "indefinite" as const } })
      : buildCycleFromHowOften(state.howOften);
    const schedule: ObligationSchedule = {
      anchor,
      cycle,
      effectiveFrom: state.effectiveFrom,
      referenceNote: state.referenceNote,
      secondary: state.secondary,
    };
    const payload: Record<string, unknown> = {
      schedule,
      comment: state.comment.trim() || null,
    };
    // BUG-1 fix: if manual anchor + has next due date, also send nextDueDate + status=confirmed
    if ((state.anchorRule === "state_variable" || state.howOften === "manual") && state.manualNextDue) {
      payload.nextDueDate = state.manualNextDue;
      payload.status = "confirmed";
    }
    mutation.mutate(payload);
  };

  const handleCancel = () => {
    setState(buildLocalState(obligation));
    setIsOpen(false);
  };

  // ── isDirty: compare current would-be-saved payload to initial payload ────
  // Includes comment field (BUG-4 fix).
  // Normalise null/undefined differences (effectiveFrom) so initial === current
  // when nothing has been changed.
  // C.1 fix: explicit short-circuit for doesntApply toggle so re-enabling a
  // not_required obligation always registers as dirty → Save enables.
  const isDirty = useMemo((): boolean => {
    // Short-circuit: doesntApply status change always makes form dirty.
    if (state.doesntApply !== (obligation.status === "not_required")) return true;

    /** Strip null/undefined values so absent vs null compares as equal. */
    const normalise = (o: unknown): unknown =>
      JSON.parse(JSON.stringify(o, (_k, v) => (v === undefined || v === null ? undefined : v)));

    let initialPayload: Record<string, unknown>;
    if (obligation.status === "not_required") {
      initialPayload = { status: "not_required", comment: "" };
    } else {
      const initSched = scheduleFromObligation(obligation);
      initialPayload = { schedule: initSched, comment: "" };
    }
    let currentPayload: Record<string, unknown>;
    if (state.doesntApply) {
      currentPayload = { status: "not_required", comment: state.comment };
    } else {
      const anchor = buildAnchorFromState(state);
      const cycle = state.phasedEnabled
        ? (state.advancedCycle.kind === "phased" ? state.advancedCycle : { kind: "phased" as const, phases: [], then: { kind: "indefinite" as const } })
        : buildCycleFromHowOften(state.howOften);
      const schedule: ObligationSchedule = { anchor, cycle, effectiveFrom: state.effectiveFrom, referenceNote: state.referenceNote, secondary: state.secondary };
      currentPayload = { schedule, comment: state.comment };
    }
    return JSON.stringify(normalise(currentPayload)) !== JSON.stringify(normalise(initialPayload));
  }, [obligation, state]);

  // ── Derived helpers ───────────────────────────────────────────────────────
  const isPhased = state.phasedEnabled;
  const simpleDisabled = state.doesntApply || isPhased;

  // Non-representable schedule (e.g. fixed_window, state_variable, phased entered via
  // Advanced — cycle.kind not in {simple, manual, indefinite}).
  const nonRepresentable = !representable && !isPhased;

  // Has any secondary filing ref note exceeding 500 chars?
  const hasRefNoteError = state.secondary.some((f) => (f.referenceNote ?? "").length > 500);

  // ── Save disabled reason for fixed_date / fixed_window incomplete inputs ──
  // Skip the check when the obligation is marked "doesn't apply" — the
  // anchor-rule fields are irrelevant in that branch and must not block save.
  const fixedDateIncomplete = !state.doesntApply && (
    (state.anchorRule === "fixed_date" && (!state.fixedMonth || !state.fixedDay)) ||
    (state.anchorRule === "fixed_window" && (!state.fixedWindowMonth || !state.fixedWindowDay))
  );

  // ── Save disabled when an "after" anchor has no offset entered ──
  // The backend requires offsetValue >= 1; a 0/blank offset would 422. Gate
  // Save with a friendly hint instead of letting the save fail. Same guard
  // as above — irrelevant in doesntApply mode.
  const offsetIncomplete = !state.doesntApply && (
    (state.anchorRule === "fy_end_offset" && state.fyEndOffsetValue < 1) ||
    (state.anchorRule === "post_event_offset" && state.postEventOffsetValue < 1)
  );

  // ── Live next-due preview ─────────────────────────────────────────────────
  const livePreviewDetail = useMemo(
    () => entity ? previewNextDueDetailed(state, entity) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.anchorRule, state.anniversaryOffsetValue, state.anniversaryOffsetUnit,
     state.fyEndOffsetValue, state.fyEndOffsetUnit, state.postEventOffsetValue,
     state.postEventOffsetUnit, state.fixedMonth, state.fixedDay, state.fixedWindowMonth,
     state.fixedWindowDay, state.howOften, state.effectiveFrom, state.manualNextDue,
     state.doesntApply, state.phasedEnabled, entity],
  );

  // ── triggerLabel from local state (Change 4) ──────────────────────────────
  const triggerLabel = useMemo((): string => {
    if (state.doesntApply) return t("status_not_required");
    if (state.howOften === "indefinite") return t("cycle_kind_indefinite");
    if (state.howOften === "manual") {
      return state.manualNextDue
        ? `${t("next_due_label")}: ${state.manualNextDue}`
        : t("cycle_kind_manual");
    }
    if (state.phasedEnabled) {
      const n = state.advancedCycle.phases?.length ?? 0;
      return n > 0 ? `${n} ${t("phased_phases_summary")}` : t("cycle_kind_phased");
    }
    const anchorShort =
      state.anchorRule === "anniversary" ? t("anchor_anniversary_short") :
      state.anchorRule === "fixed_date" ? (state.fixedMonth && state.fixedDay ? `${state.fixedMonth}/${state.fixedDay}` : t("anchor_fixed_date")) :
      state.anchorRule === "state_variable" ? t("anchor_manual_short") :
      t(`anchor_${state.anchorRule}`);
    return `${anchorShort} · ${state.howOften}`;
  }, [state, t]);

  const commentId = `${uid}-comment`;

  return (
    <div className="w-full">
      {/* ── Trigger button ──────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        className={cn(
          "flex items-center gap-2.5 cursor-pointer select-none",
          "h-9 min-w-[182px] w-full px-3 border-[1.5px] border-border rounded-[9px] bg-surface",
          "text-[13.5px] font-semibold text-text transition-[border-color,box-shadow]",
          "hover:border-accent",
          "focus:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/15",
          isOpen && "border-accent ring-[3px] ring-accent/15",
        )}
      >
        <Settings2 className="h-[14px] w-[14px] text-muted shrink-0" aria-hidden />
        <span className="flex-1 truncate text-left">{triggerLabel}</span>
        <ChevronDown
          className={cn(
            "h-[15px] w-[15px] text-muted transition-transform shrink-0",
            isOpen && "rotate-180",
          )}
          aria-hidden
        />
      </button>

      {/* ── Expanded panel ──────────────────────────────────────────────── */}
      {isOpen && (
        <div className="mt-2 bg-surface border border-border rounded-[10px] shadow-pop p-4 space-y-5">

          {/* ── Reference text (editable) ────────────────────────────────── */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <SectionLabel>{t("reference_note_label")}</SectionLabel>
              {!state.refNoteEditing && (
                <button
                  type="button"
                  aria-label={t("edit_reference_note_aria")}
                  onClick={() => setState((s) => ({ ...s, refNoteEditing: true }))}
                  className="inline-flex items-center gap-1 text-[11.5px] text-dim hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20 rounded"
                >
                  <Pencil className="h-3 w-3" aria-hidden /> {t("edit_button_short")}
                </button>
              )}
            </div>
            {state.refNoteEditing ? (
              <div className="space-y-2">
                <textarea
                  value={state.referenceNote}
                  onChange={(e) => setState((s) => ({ ...s, referenceNote: e.target.value }))}
                  placeholder={t("reference_note_placeholder")}
                  rows={2}
                  className={cn(
                    "w-full px-3 py-2 rounded-lg border border-border bg-surface-2 text-[13px] text-base resize-none",
                    "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
                  )}
                />
                <button
                  type="button"
                  onClick={() => setState((s) => ({ ...s, refNoteEditing: false }))}
                  className="text-[11.5px] text-dim hover:text-text"
                >
                  {t("done_editing_button")}
                </button>
              </div>
            ) : (
              <p className={cn("text-[12px] italic", state.referenceNote ? "text-muted" : "text-dim")}>
                {state.referenceNote
                  ? <RenderReferenceText text={state.referenceNote} />
                  : <span>{t("reference_note_empty")}</span>
                }
              </p>
            )}
          </div>

          {/* ── Non-representable notice ─────────────────────────────────── */}
          {nonRepresentable && (
            <div className="rounded-lg bg-amber-soft border border-[#fde9c8] px-3 py-2 text-[12px] text-amber-text">
              {t("schedule_summary_fallback")}
            </div>
          )}

          {/* ── Starts from + How often ──────────────────────────────────── */}
          {!nonRepresentable && (
            <div className={cn("space-y-4", simpleDisabled && "opacity-50 pointer-events-none")}>

              {/* Starts from — anchor selector */}
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <label
                    htmlFor={`${uid}-starts-from`}
                    className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim"
                  >
                    {t("starts_from_label")}
                  </label>
                  <InfoTooltip content={t("starts_from_tooltip")} />
                </div>
                <StyledSelect
                  id={`${uid}-starts-from`}
                  value={state.anchorRule}
                  onChange={(v) =>
                    setState((s) => ({
                      ...s,
                      anchorRule: v as AnchorRule,
                      // Reset sub-inputs when switching anchor types
                      anniversaryOffsetValue: null,
                      anniversaryOffsetUnit: "days",
                      fyEndOffsetValue: 0,
                      fyEndOffsetUnit: "months",
                      fyEndRangeMax: null,
                      postEventOffsetValue: 0,
                      postEventOffsetUnit: "days",
                      fixedMonth: null,
                      fixedDay: null,
                      fixedWindowMonth: null,
                      fixedWindowDay: null,
                    }))
                  }
                  disabled={simpleDisabled}
                  className="w-full max-w-xs"
                >
                  {/* Sentinel option for "none" anchor — rendered only when the current obligation
                      uses anchor.rule="none" (country has no obligation). Disabled to prevent
                      free selection; user must use the "Doesn't apply" checkbox instead. */}
                  {state.anchorRule === "none" && (
                    <option value="none" disabled>
                      {t("anchor_none_disabled_label")}
                    </option>
                  )}
                  <option value="anniversary">{t("anchor_anniversary")}</option>
                  <option value="fy_end_offset">{t("anchor_fy_end_offset")}</option>
                  <option value="post_event_offset">{t("anchor_post_event_offset")}</option>
                  <option value="fixed_date">{t("anchor_fixed_date")}</option>
                  <option value="fixed_window">{t("anchor_fixed_window")}</option>
                  <option value="state_variable">{t("anchor_manual_label")}</option>
                </StyledSelect>

                {/* ── Per-anchor sub-inputs ── */}

                {/* Anniversary — optional offset inputs (e.g. UK NAR1: 42 days after anniversary) */}
                {state.anchorRule === "anniversary" && !state.doesntApply && (
                  <div className="mt-3 space-y-3">
                    <div className="flex flex-wrap items-end gap-2">
                      <div>
                        <label className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1">
                          {t("cse_anniversary_offset_value")}
                        </label>
                        <input
                          type="number"
                          min={1}
                          value={state.anniversaryOffsetValue ?? ""}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            setState((s) => ({
                              ...s,
                              anniversaryOffsetValue: Number.isNaN(n) ? null : Math.max(1, n),
                            }));
                          }}
                          placeholder={t("cse_anniversary_offset_placeholder")}
                          className="text-base h-9 w-20 px-3 rounded-lg border border-border bg-surface text-[13px] focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none"
                          aria-label={t("cse_anniversary_offset_value")}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1">
                          {t("cse_unit")}
                        </label>
                        <StyledSelect
                          value={state.anniversaryOffsetUnit}
                          onChange={(v) =>
                            setState((s) => ({ ...s, anniversaryOffsetUnit: v as "days" | "months" }))
                          }
                          aria-label={t("cse_unit")}
                        >
                          <option value="days">{t("cse_unit_days")}</option>
                          <option value="months">{t("interval_unit_months")}</option>
                        </StyledSelect>
                      </div>
                    </div>
                    <p className="text-[11.5px] text-muted">
                      {state.anniversaryOffsetValue != null
                        ? t("anchor_anniversary_offset_helper", {
                            n: state.anniversaryOffsetValue,
                            unit: state.anniversaryOffsetUnit === "months"
                              ? t("interval_unit_months")
                              : t("cse_unit_days"),
                          })
                        : t("anchor_anniversary_helper")}
                    </p>
                  </div>
                )}

                {/* fy_end_offset — N + unit (+ optional range max for days) */}
                {state.anchorRule === "fy_end_offset" && !state.doesntApply && (
                  <div className="mt-3 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[12px] text-muted">{t("cse_fy_end_phrase_pre")}</span>
                      <div>
                        <label className="sr-only">
                          {t("cse_offset_value")}
                        </label>
                        <input
                          type="number"
                          min={1}
                          value={state.fyEndOffsetValue || ""}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            setState((s) => ({ ...s, fyEndOffsetValue: Number.isNaN(n) ? 0 : Math.max(1, n) }));
                          }}
                          className="text-base h-9 w-20 px-3 rounded-lg border border-border bg-surface text-[13px] focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none"
                          placeholder="6"
                          aria-label={t("cse_offset_value")}
                        />
                      </div>
                      <div>
                        <label className="sr-only">
                          {t("cse_unit")}
                        </label>
                        <StyledSelect
                          value={state.fyEndOffsetUnit}
                          onChange={(v) => setState((s) => ({ ...s, fyEndOffsetUnit: v as "months" | "days" }))}
                          aria-label={t("cse_unit")}
                        >
                          <option value="months">{t("interval_unit_months")}</option>
                          <option value="days">{t("cse_unit_days")}</option>
                        </StyledSelect>
                      </div>
                      <span className="text-[12px] text-muted">{t("cse_fy_end_phrase_post")}</span>
                    </div>
                    {/* Range max — only when unit is days (Italy: 120–180) */}
                    {state.fyEndOffsetUnit === "days" && (
                      <div>
                        <label className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1">
                          {t("cse_range_max")}
                        </label>
                        <input
                          type="number"
                          min={1}
                          value={state.fyEndRangeMax ?? ""}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            setState((s) => ({ ...s, fyEndRangeMax: Number.isNaN(n) ? null : Math.max(1, n) }));
                          }}
                          className="text-base h-9 w-24 px-3 rounded-lg border border-border bg-surface text-[13px] focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none"
                          placeholder={t("cse_range_max_placeholder")}
                          aria-label={t("cse_range_max")}
                        />
                      </div>
                    )}
                    <p className="text-[11.5px] text-muted">
                      {t("anchor_fy_end_offset_helper", {
                        n: state.fyEndOffsetValue || 0,
                        unit: state.fyEndOffsetUnit === "months" ? t("interval_unit_months") : t("cse_unit_days"),
                      })}
                    </p>
                    {state.fyEndOffsetValue < 1 && (
                      <p className="text-[11px] text-amber-text">{t("offset_required_hint")}</p>
                    )}
                  </div>
                )}

                {/* post_event_offset — N + unit */}
                {state.anchorRule === "post_event_offset" && !state.doesntApply && (
                  <div className="mt-3 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[12px] text-muted">{t("cse_post_event_phrase_pre")}</span>
                      <div>
                        <label className="sr-only">
                          {t("cse_offset_value")}
                        </label>
                        <input
                          type="number"
                          min={1}
                          value={state.postEventOffsetValue || ""}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            setState((s) => ({ ...s, postEventOffsetValue: Number.isNaN(n) ? 0 : Math.max(1, n) }));
                          }}
                          className="text-base h-9 w-20 px-3 rounded-lg border border-border bg-surface text-[13px] focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none"
                          placeholder="15"
                          aria-label={t("cse_offset_value")}
                        />
                      </div>
                      <div>
                        <label className="sr-only">
                          {t("cse_unit")}
                        </label>
                        <StyledSelect
                          value={state.postEventOffsetUnit}
                          onChange={(v) => setState((s) => ({ ...s, postEventOffsetUnit: v as "months" | "days" }))}
                          aria-label={t("cse_unit")}
                        >
                          <option value="days">{t("cse_unit_days")}</option>
                          <option value="months">{t("interval_unit_months")}</option>
                        </StyledSelect>
                      </div>
                      <span className="text-[12px] text-muted">{t("cse_post_event_phrase_post")}</span>
                    </div>
                    <p className="text-[11.5px] text-muted">
                      {t("anchor_post_event_offset_helper", {
                        n: state.postEventOffsetValue || 0,
                        unit: state.postEventOffsetUnit === "months" ? t("interval_unit_months") : t("cse_unit_days"),
                      })}
                    </p>
                    {state.postEventOffsetValue < 1 && (
                      <p className="text-[11px] text-amber-text">{t("offset_required_hint")}</p>
                    )}
                  </div>
                )}

                {/* fixed_date — month name select + day (with dynamic day clamp per C.3) */}
                {state.anchorRule === "fixed_date" && !state.doesntApply && (
                  <div className="mt-3 space-y-3">
                    <div className="flex flex-wrap gap-2">
                      <div>
                        <label htmlFor={`${uid}-fixed-month`} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1">
                          {t("cse_month")}
                        </label>
                        <select
                          id={`${uid}-fixed-month`}
                          value={state.fixedMonth ?? ""}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            if (Number.isNaN(n)) { setState((s) => ({ ...s, fixedMonth: null })); return; }
                            const m = Math.min(12, Math.max(1, n));
                            const maxDay = DAYS_IN_MONTH[m] ?? 31;
                            setState((s) => ({
                              ...s,
                              fixedMonth: m,
                              fixedDay: s.fixedDay !== null ? Math.min(s.fixedDay, maxDay) : null,
                            }));
                          }}
                          aria-label={t("cse_month")}
                          className="h-9 px-3 rounded-lg border border-border bg-surface text-[13px] focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none"
                        >
                          <option value="">—</option>
                          {MONTH_KEYS_OBL.map((mk, idx) => (
                            <option key={mk} value={idx + 1}>{tTF(mk)}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1">
                          {t("cse_day")}
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={DAYS_IN_MONTH[state.fixedMonth ?? 1] ?? 31}
                          value={state.fixedDay ?? ""}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            const maxDay = DAYS_IN_MONTH[state.fixedMonth ?? 1] ?? 31;
                            setState((s) => ({
                              ...s,
                              fixedDay: Number.isNaN(n) ? null : Math.min(maxDay, Math.max(1, n)),
                            }));
                          }}
                          className="text-base h-9 w-20 px-3 rounded-lg border border-border bg-surface text-[13px] focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none"
                          placeholder="1"
                          aria-label={t("cse_day")}
                        />
                      </div>
                    </div>
                    <p className="text-[11.5px] text-muted">
                      {(state.fixedMonth && state.fixedDay)
                        ? t("anchor_fixed_date_helper", {
                            month: monthName(state.fixedMonth),
                            day: state.fixedDay,
                          })
                        : t("anchor_fixed_date_helper", {
                            month: "?",
                            day: "?",
                          })}
                    </p>
                    {(!state.fixedMonth || !state.fixedDay) && !state.doesntApply && (
                      <p className="text-[11px] text-amber-text mt-1">{t("fixed_date_required_hint")}</p>
                    )}
                  </div>
                )}

                {/* fixed_window — window-open month name select + day */}
                {state.anchorRule === "fixed_window" && !state.doesntApply && (
                  <div className="mt-3 space-y-3">
                    <div className="flex flex-wrap gap-2">
                      <div>
                        <label htmlFor={`${uid}-fixed-window-month`} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1">
                          {t("cse_window_open_month")}
                        </label>
                        <select
                          id={`${uid}-fixed-window-month`}
                          value={state.fixedWindowMonth ?? ""}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            if (Number.isNaN(n)) { setState((s) => ({ ...s, fixedWindowMonth: null })); return; }
                            const m = Math.min(12, Math.max(1, n));
                            const maxDay = DAYS_IN_MONTH[m] ?? 31;
                            setState((s) => ({
                              ...s,
                              fixedWindowMonth: m,
                              fixedWindowDay: s.fixedWindowDay !== null ? Math.min(s.fixedWindowDay, maxDay) : null,
                            }));
                          }}
                          aria-label={t("cse_window_open_month")}
                          className="h-9 px-3 rounded-lg border border-border bg-surface text-[13px] focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none"
                        >
                          <option value="">—</option>
                          {MONTH_KEYS_OBL.map((mk, idx) => (
                            <option key={mk} value={idx + 1}>{tTF(mk)}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-1">
                          {t("cse_window_open_day")}
                        </label>
                        <input
                          type="number"
                          min={1}
                          max={DAYS_IN_MONTH[state.fixedWindowMonth ?? 1] ?? 31}
                          value={state.fixedWindowDay ?? ""}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            const maxDay = DAYS_IN_MONTH[state.fixedWindowMonth ?? 1] ?? 31;
                            setState((s) => ({
                              ...s,
                              fixedWindowDay: Number.isNaN(n) ? null : Math.min(maxDay, Math.max(1, n)),
                            }));
                          }}
                          className="text-base h-9 w-20 px-3 rounded-lg border border-border bg-surface text-[13px] focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none"
                          placeholder="1"
                          aria-label={t("cse_window_open_day")}
                        />
                      </div>
                    </div>
                    <p className="text-[11.5px] text-muted">
                      {(state.fixedWindowMonth && state.fixedWindowDay)
                        ? t("anchor_fixed_window_helper", {
                            month: monthName(state.fixedWindowMonth),
                            day: state.fixedWindowDay,
                          })
                        : t("anchor_fixed_window_unset_helper")}
                    </p>
                    <p className="text-[11px] text-dim">{t("anchor_fixed_window_note")}</p>
                    {(!state.fixedWindowMonth || !state.fixedWindowDay) && !state.doesntApply && (
                      <p className="text-[11px] text-amber-text mt-1">{t("fixed_date_required_hint")}</p>
                    )}
                  </div>
                )}
              </div>

              {/* How often */}
              <div>
                <label
                  htmlFor={`${uid}-how-often`}
                  className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-2"
                >
                  <Term term="how_often">{t("how_often_label")}</Term>
                </label>
                {state.phasedEnabled ? (
                  <div className="rounded-md bg-surface-2 border border-border-2 px-3 py-2 text-[12.5px] text-muted">
                    {t("how_often_phased_note")}
                  </div>
                ) : (
                  <StyledSelect
                    id={`${uid}-how-often`}
                    value={state.howOften}
                    onChange={(v) => setState((s) => ({ ...s, howOften: v as HowOftenKey }))}
                    disabled={simpleDisabled}
                    className="w-full max-w-xs"
                  >
                    {HOW_OFTEN_OPTIONS.map((k) => (
                      <option key={k} value={k}>{t(`how_often_${k}`)}</option>
                    ))}
                  </StyledSelect>
                )}
              </div>

              {/* Next renewal date — shown when anchor is manual (state_variable) or howOften is manual */}
              {(state.anchorRule === "state_variable" || state.howOften === "manual") && (
                <div>
                  <div className="flex items-center gap-1 mb-2">
                    <label className="text-[10px] font-bold tracking-[.05em] uppercase text-dim">
                      {t("next_due_date_label")}
                    </label>
                    <InfoTooltip content={t("next_due_date_tooltip")} />
                  </div>
                  <InlineDatePicker
                    value={state.manualNextDue}
                    onChange={(v) => setState((s) => ({ ...s, manualNextDue: v }))}
                    placeholder={t("next_due_date_placeholder")}
                    ariaLabel={t("next_due_date_label")}
                  />
                  <p className="text-[11px] text-dim mt-1">{t("next_due_date_helper")}</p>
                </div>
              )}

              {/* ── Live next-due preview ── */}
              {!state.doesntApply && !state.phasedEnabled && state.howOften !== "indefinite" && entity && (
                <div
                  data-testid="cse-live-preview"
                  className="rounded-lg bg-surface-2 border border-border-2 px-3 py-2 text-[12px] space-y-1"
                >
                  {livePreviewDetail?.nextDue ? (
                    <>
                      {/* Anchor transparency line */}
                      {livePreviewDetail.anchorDate && (
                        <p className="text-dim">
                          {state.anchorRule === "anniversary" && (
                            <>
                              {t("live_preview_anchor_anniversary", { anchorDate: livePreviewDetail.anchorDate })}
                              {state.anniversaryOffsetValue != null && (
                                <span>
                                  {" "}
                                  {t("live_preview_anchor_anniversary_offset", {
                                    n: state.anniversaryOffsetValue,
                                    unit: state.anniversaryOffsetUnit === "months"
                                      ? t("interval_unit_months")
                                      : t("cse_unit_days"),
                                  })}
                                </span>
                              )}
                            </>
                          )}
                          {state.anchorRule === "fy_end_offset" && (
                            t("live_preview_anchor_fy_end", {
                              anchorDate: livePreviewDetail.anchorDate,
                              n: state.fyEndOffsetValue || 0,
                              unit: state.fyEndOffsetUnit === "months"
                                ? t("interval_unit_months")
                                : t("cse_unit_days"),
                            })
                          )}
                          {state.anchorRule === "post_event_offset" && (
                            t("live_preview_anchor_post_event", {
                              anchorDate: livePreviewDetail.anchorDate,
                              n: state.postEventOffsetValue || 0,
                              unit: state.postEventOffsetUnit === "months"
                                ? t("interval_unit_months")
                                : t("cse_unit_days"),
                            })
                          )}
                        </p>
                      )}
                      {/* Next due */}
                      <div className="flex items-center gap-1.5">
                        <span className="text-dim font-medium">{t("live_preview_label")}</span>
                        <strong className="text-accent font-semibold">{livePreviewDetail.nextDue}</strong>
                      </div>
                    </>
                  ) : (
                    /* Specific missing-anchor message with Dates & Duration scroll link */
                    <div className="flex flex-wrap items-center gap-1 text-dim italic">
                      <span>
                        {state.anchorRule === "anniversary"
                          ? t("live_preview_missing_anchor_anniversary")
                          : state.anchorRule === "fy_end_offset"
                          ? t("live_preview_missing_anchor_fy_end")
                          : state.anchorRule === "post_event_offset"
                          ? t("live_preview_missing_anchor_post_event")
                          : t("live_preview_missing_anchor")}
                      </span>
                      {(state.anchorRule === "anniversary" ||
                        state.anchorRule === "fy_end_offset" ||
                        state.anchorRule === "post_event_offset") && (
                        <button
                          type="button"
                          onClick={() =>
                            document
                              .getElementById("dates-duration")
                              ?.scrollIntoView({ behavior: "smooth" })
                          }
                          className={cn(
                            "underline text-accent not-italic cursor-pointer",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20 rounded",
                            "min-h-[44px] inline-flex items-center",
                          )}
                        >
                          {t("live_preview_dates_duration_link")}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Doesn't apply toggle ─────────────────────────────────────── */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2.5">
              <input
                id={`${uid}-doesnt-apply`}
                type="checkbox"
                checked={state.doesntApply}
                onChange={(e) => setState((s) => ({ ...s, doesntApply: e.target.checked }))}
                className="h-4 w-4 rounded border-border accent-accent focus:outline-none focus:ring-2 focus:ring-accent/20 cursor-pointer"
                aria-describedby={`${uid}-doesnt-apply-helper`}
              />
              <label htmlFor={`${uid}-doesnt-apply`} className="text-[13px] font-medium text-text cursor-pointer select-none">
                <Term term="not_required_obligation">{t("doesnt_apply_toggle_label")}</Term>
              </label>
              <InfoTooltip content={t("sf_tooltip_doesnt_apply")} />
            </div>
            <p id={`${uid}-doesnt-apply-helper`} className="text-[11.5px] text-dim pl-[26px]">
              {t("doesnt_apply_helper")}
            </p>
          </div>

          {/* ── More options collapsible ─────────────────────────────────── */}
          <div className="border-t border-border-2 pt-3">
            <button
              type="button"
              onClick={() => setState((s) => ({ ...s, moreOptionsOpen: !s.moreOptionsOpen }))}
              aria-expanded={state.moreOptionsOpen}
              className="flex items-center gap-2 text-[12px] font-semibold text-muted hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20 rounded"
            >
              <ChevronDown
                className={cn("h-3.5 w-3.5 transition-transform", state.moreOptionsOpen && "rotate-180")}
                aria-hidden
              />
              {t("more_options_label")}
              {(() => {
                const phasedCount = state.phasedEnabled ? (state.advancedCycle.phases?.length ?? 0) : 0;
                const sfCount = state.secondary.length;
                if (phasedCount === 0 && sfCount === 0) return null;
                const parts: string[] = [];
                if (phasedCount > 0) parts.push(`${phasedCount} phase${phasedCount !== 1 ? "s" : ""}`);
                if (sfCount > 0) parts.push(`${sfCount} additional filing${sfCount !== 1 ? "s" : ""}`);
                return <span className="ml-1.5 text-[11px] text-accent font-normal">({parts.join(" · ")})</span>;
              })()}
            </button>

            {state.moreOptionsOpen && (
              <div className="mt-3 space-y-5">
                {/* ── Phased schedule ── */}
                <div>
                  <div className="flex items-center gap-2.5 mb-2">
                    <input
                      id={`${uid}-phased-toggle`}
                      type="checkbox"
                      checked={state.phasedEnabled}
                      onChange={(e) => {
                        const on = e.target.checked;
                        setState((s) => ({
                          ...s,
                          phasedEnabled: on,
                          advancedCycle: on && s.advancedCycle.kind !== "phased"
                            ? { kind: "phased", phases: [{ durationYears: 1, cycle: "1y" }], then: { kind: "indefinite" } }
                            : s.advancedCycle,
                        }));
                      }}
                      className="h-4 w-4 rounded border-border accent-accent focus:outline-none focus:ring-2 focus:ring-accent/20 cursor-pointer"
                    />
                    <label htmlFor={`${uid}-phased-toggle`} className="text-[13px] font-medium text-text cursor-pointer select-none">
                      {t("phased_toggle_label")}
                    </label>
                    <InfoTooltip content={t("sf_tooltip_phased")} />
                  </div>
                  <p className="text-[11.5px] text-dim pl-[26px] mb-2">{t("phased_toggle_helper")}</p>
                  {state.phasedEnabled && (
                    <PhasedCycleEditor
                      cycle={state.advancedCycle.kind === "phased" ? state.advancedCycle : { kind: "phased", phases: [], then: { kind: "indefinite" } }}
                      onChange={(c) => setState((s) => ({ ...s, advancedCycle: c }))}
                    />
                  )}
                </div>

                {/* ── Secondary filings (editable) ── */}
                <SecondaryFilingEditor
                  filings={state.secondary}
                  onChange={(v) => setState((s) => ({ ...s, secondary: v }))}
                />
              </div>
            )}
          </div>

          {/* ── Comment field ─────────────────────────────────────────────── */}
          <div>
            <label htmlFor={commentId} className="block text-[10px] font-bold tracking-[.05em] uppercase text-dim mb-2">
              {t("mark_renewed_comment_label")}
            </label>
            <input
              id={commentId}
              type="text"
              value={state.comment}
              onChange={(e) => setState((s) => ({ ...s, comment: e.target.value }))}
              placeholder={t("amend_date_comment_placeholder")}
              className={cn(
                "h-9 w-full px-3 rounded-lg border border-border bg-surface-2 text-[13.5px]",
                "focus:bg-surface focus:border-accent focus:ring-2 focus:ring-accent/14 focus:outline-none",
              )}
            />
          </div>

          {/* ── Save / Cancel ─────────────────────────────────────────────── */}
          <div className="flex items-center gap-2 pt-1 border-t border-border-2">
            <SaveButtonWithHint
              isPending={mutation.isPending}
              isDirty={isDirty && !hasRefNoteError && !fixedDateIncomplete && !offsetIncomplete}
              missingComment={false}
              onClick={handleSave}
              saveLabel={t("save_button")}
            />
            <Button variant="secondary" onClick={handleCancel}>
              <X className="h-3.5 w-3.5" aria-hidden /> {t("cancel_button")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
